import { useState } from "react";
import { Headphones, Mic2, Trash2 } from "lucide-react";
import WaveCanvas from "@/components/recstudio/WaveCanvas";
import { TRACK_KINDS } from "@/lib/recStudioApi";

const kindLabel = (k) => TRACK_KINDS.find((x) => x.key === k)?.label || "Vocal";
const ms = (on, tone) => `grid h-7 w-7 place-items-center rounded-lg border font-mono text-[11px] font-bold transition-colors duration-150 ${on ? tone : "border-white/10 text-white/55 hover:text-white"}`;

/** Mute · solo · volume for any session part. */
export function MixBits({ id, part, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" data-testid={`rec-mute-${id}`} aria-pressed={part.muted} onClick={() => onChange({ muted: !part.muted })} className={ms(part.muted, "border-rose-300/60 bg-rose-500/25 text-rose-50")}>M</button>
      <button type="button" data-testid={`rec-solo-${id}`} aria-pressed={part.solo} onClick={() => onChange({ solo: !part.solo })} className={ms(part.solo, "border-amber-300/60 bg-amber-400/25 text-amber-50")}>S</button>
      <input type="range" data-testid={`rec-volume-${id}`} aria-label="Volume" min={0} max={100} step={1} value={part.volume} onChange={(e) => onChange({ volume: Number(e.target.value) })} className="h-1.5 w-full min-w-[70px] cursor-pointer accent-rose-300" />
      <span className="w-8 text-right font-mono text-[10px] text-white/55">{Math.round(part.volume)}</span>
    </div>
  );
}

/** One vocal track: record-here target, name, takes, mix, mini waveform. */
export default function TrackRow({ track, selected, buffer, duration, onSelect, onChange, onPickTake, onRemove }) {
  const [name, setName] = useState(track.name);
  return (
    <div data-testid={`rec-track-${track.id}`} data-selected={selected ? "1" : "0"}
      className={`rounded-2xl border p-3 transition-colors duration-200 ${selected ? "border-rose-300/50 bg-rose-500/[0.08]" : "border-white/[0.08] bg-black/25 hover:bg-white/[0.04]"}`}>
      <div className="flex items-center gap-2">
        <button type="button" role="radio" aria-checked={selected} data-testid={`rec-track-select-${track.id}`} onClick={onSelect} title="Record onto this track"
          className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border ${selected ? "border-rose-300 bg-rose-500 text-white" : "border-white/15 text-white/50"}`}>
          {track.kind === "speech" ? <Headphones className="h-3.5 w-3.5" /> : <Mic2 className="h-3.5 w-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <input data-testid={`rec-track-name-${track.id}`} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} onBlur={() => name.trim() && name !== track.name && onChange({ name: name.trim() })}
            className="w-full truncate bg-transparent font-display text-[14px] font-semibold focus:outline-none" />
          <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-white/40">{kindLabel(track.kind)} · {track.takes.length} take{track.takes.length === 1 ? "" : "s"}</p>
        </div>
        <button type="button" data-testid={`rec-track-remove-${track.id}`} onClick={onRemove} aria-label="Remove track" className="text-white/35 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      {track.takes.length ? (
        <select data-testid={`rec-track-take-${track.id}`} value={track.active_take_id || ""} onChange={(e) => onPickTake(e.target.value || null)}
          className="mt-2 w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-[12px] text-white focus:outline-none">
          <option value="">No take (silent)</option>
          {[...track.takes].reverse().map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      ) : null}
      {buffer ? <div className="mt-2"><WaveCanvas buffer={buffer} duration={duration} height={30} /></div> : null}
      <div className="mt-2"><MixBits id={track.id} part={track} onChange={onChange} /></div>
    </div>
  );
}
